import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import { Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { getResources } from "./redux/Resource/resourceCalls";
import { useEffect } from "react";
import { switchTheme } from "./styles/CustomSwitch";
import { useTranslation } from "react-i18next";
import Home from './pages/Home'
import HomeSO from "./pages/SignedOut/HomeSO";
import AppLayout from './components/AppLayout'
import NavComplete from './components/NavComplete';
import NavSignedOut from './components/NavSignedOut'
import QuickSStartGuide from "./pages/QuickStartGuide";
import { Amplify } from 'aws-amplify';


Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
      loginWith: {
        email: true
      }
    }
  }
});



const Help = lazy(() => import('./pages/Help'))
const ViewDocument = lazy(() => import('./pages/View/ViewDocument'))
const Article = lazy(() => import('./components/Article'))
const FreeTrial = lazy(() => import('./pages/SignedOut/FreeTrial'))
const Video = lazy(() => import('./pages/SignedOut/Video'))
// const SignupCompanySuccess = lazy(() => import('./pages/SignedOut/SignupCompanySuccess'))

const Login = lazy(() => import('./pages/SignedOut/Login'))
const SignupCompany = lazy(() => import('./pages/SignedOut/SignupCompany'))
const POHistory = lazy(() => import('./pages/POHistory'))
const QuoteHistory = lazy(() => import('./pages/QuoteHistory'))
const TemplateHistory = lazy(() => import('./pages/TemplateHistory'))
const InvoiceHistory = lazy(() => import('./pages/InvoiceHistory'))
const DraftHistory = lazy(() => import('./pages/DraftHistory'))
const ProformaHistory = lazy(() => import('./pages/ProformaHistory'))
const Contacts = lazy(() => import('./pages/Contacts'))
const Products = lazy(() => import('./pages/Products'))
const Resources = lazy(() => import('./pages/Resources'))
const Settings = lazy(() => import('./pages/Settings'))
const CreateQuote = lazy(() => import('./pages/Create/CreateQuote'))
const ChooseTemplate = lazy(() => import('./pages/ChooseTemplate'))
const CreateTemplate = lazy(() => import('./pages/Create/CreateTemplate'))
const CreatePO = lazy(() => import('./pages/Create/CreatePO'))
const CreateProforma = lazy(() => import('./pages/Create/CreateProforma'))
const SignupUser = lazy(() => import('./pages/SignedOut/SignupUser'))
const EditPO = lazy(() => import('./pages/Edit/EditPO'))
const EditQuote = lazy(() => import('./pages/Edit/EditQuote'))
const EditInvoice = lazy(() => import('./pages/Edit/EditInvoice'))
const EditOptions = lazy(() => import('./pages/Edit/EditOptions'))
const ReportBug = lazy(() => import('./pages/ReportBug'))
const Features = lazy(() => import('./pages/SignedOut/Features'))
const Pricing = lazy(() => import('./pages/SignedOut/Pricing'))
const AboutUs = lazy(() => import('./pages/SignedOut/AboutUs'))
const ContactUs = lazy(() => import('./pages/SignedOut/ContactUs'))
const NotFound = lazy(() => import('./pages/NotFound'))

const theme = extendTheme({
  components: {
    Switch: switchTheme
  }
})


function App() {
  const { user_id } = useSelector((state) => state.auth)
  const { account_id } = useSelector((state) => state.account)
  const { status } = useSelector((state) => state.account.subscription)
  const { trial_expiration } = useSelector((state) => state.account.subscription)
  const { i18n } = useTranslation()
  const { language } = useSelector((state) => state.auth)
  const { user_role } = useSelector((state) => state.auth)
  const location = useLocation()
  const dispatch = useDispatch()
  const today = new Date()
  const trial_exp = new Date(trial_expiration) ?? null

  useEffect(() => {
    // HR - refetch resources every hour so that signed URLs do not expire
    const resourceInterval = setInterval(() => {
      if (account_id) {
        getResources(account_id, dispatch)
      }
    }, 3600000)
    return () => clearInterval(resourceInterval)
  }, [])

  // HR - GTM Tracking for page views
  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: location.pathname + location.search,
        page_title: location.pathname, // Or a more dynamic title based on your app
      })
    }
  }, [location])

  useEffect(() => {
    if (user_id) {
      // HR - update internal language on login
      i18n.changeLanguage(language)
      localStorage.setItem('rapidtoolz_language', language);
    }
  }, [user_id, language])


  return (
    <>
      {/* HR - ChakraProvider is necessary to use chakraUI components */}
      <ChakraProvider theme={theme}>
        {/* HR - AppLayout used to check for inactivity and logout user */}
        <AppLayout>
          {/* HR - NavComplete includes the navbar at the top, and the drawer on the left */}
          {user_id ? <NavComplete /> : <NavSignedOut />}
          <Suspense>
            <Routes>
              {/* HR - if user_id exists (redux state) then we show signed-in home page, otherwise show signup */}
              {user_id ?
                <>
                  <Route exact path="/" element={<Home />} />
                </>
                : <Route exact path="/" element={<HomeSO />} />
              }
              {(user_id && (status === "active" || (status === "trial" && today < trial_exp))) && <>
                <Route exact path="/create/quote" element={<CreateQuote fromTemplate={false} />} />
                <Route exact path="/create/quote/template" element={<CreateQuote fromTemplate={true} />} />
                <Route exact path="/create/purchaseorder" element={<CreatePO />} />
                {/* <Route exact path="/create/proforma" element={<CreateProforma />} /> */}
                <Route exact path="/edit/options" element={<EditOptions />} />
                <Route exact path="/edit/quote" element={<EditQuote isTemplate={false} />} />
                <Route exact path="/edit/purchaseorder" element={<EditPO />} />
                <Route exact path="/edit/invoice" element={<EditInvoice />} />
                {/* HR - only upper level roles can create and edit templates */}
                {user_role !== "user" &&
                  <>
                    <Route exact path="/edit/template" element={<EditQuote isTemplate={true} />} />
                    <Route exact path="/create/template" element={<CreateTemplate />} />
                  </>
                }
                <Route exact path="/view/quote" element={<ViewDocument />} />
                <Route exact path="/view/template" element={<ViewDocument />} />
                <Route exact path="/view/purchaseorder" element={<ViewDocument />} />
                <Route exact path="/view/invoice" element={<ViewDocument />} />
                <Route exact path="/history/quote" element={<QuoteHistory />} />
                <Route exact path="/history/template" element={<TemplateHistory />} />
                <Route exact path="/history/purchaseorder" element={<POHistory />} />
                <Route exact path="/history/invoice" element={<InvoiceHistory />} />
                {/* <Route exact path="/history/proforma" element={<ProformaHistory />} /> */}
                <Route exact path="/history/draft" element={<DraftHistory />} />
                <Route exact path="/contacts" element={<Contacts />} />
                <Route exact path="/products" element={<Products />} />
                <Route exact path="/resources" element={<Resources />} />
                <Route exact path="/settings" element={<Settings />} />
                <Route exact path="/reportbug" element={<ReportBug />} />
              </>}
              <Route exact path="/signup/user" element={<SignupUser />} />
              <Route exact path="/signup/company" element={<SignupCompany />} />
              <Route exact path="/login" element={<Login />} />
              {/* <Route exact path="/signup/company/success" element={<SignupCompanySuccess />} /> */}
              <Route exact path="/features" element={<Features />} />
              <Route exact path="/pricing" element={<Pricing />} />
              <Route exact path="/free-trial" element={<FreeTrial />} />
              <Route exact path="/howitworks" element={<Video />} />
              <Route exact path="/aboutus" element={<AboutUs />} />
              <Route exact path="/contactus" element={<ContactUs />} />
              {/* <Route exact path="/help" element={<Help />} /> */}
              {/* <Route exact path="/help/:id" element={<Article />} /> */}
              <Route exact path="/quick_start_guide" element={<QuickSStartGuide />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AppLayout>
      </ChakraProvider>
    </>
  );
}

export default App;
